import { type FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Combobox } from '@headlessui/react';
import { MapPinIcon } from '@heroicons/react/24/outline';
import cn from 'classnames';

import type { LocationSuggestion } from '~/services/api/community/types';

import {
  formatLocationSuggestion,
  type GeolocationChange,
  hasValidCoordinates,
  isNullableString,
} from '../../_utils/geolocationField';
import { FieldStatus, fieldStatusId } from './FieldStatus';
import { PillButton } from './PillButton';

const SUGGESTIONS_PATH = '/community_location_suggestions';
const DEBOUNCE_MS = 250;
const MIN_QUERY_LENGTH = 2;

export type LocationChange = {
  location: string;
  geolocation: GeolocationChange;
};

interface LocationPickerProps {
  id: string;
  label: string;
  value: string;
  onChange: (change: LocationChange) => void;
  placeholder?: string;
  hint?: string;
  error?: string;
  disabled?: boolean;
  required?: boolean;
  maxLength?: number;
  /** Offered as a one-tap fill while the field is empty, never written into it: a fill is the member's pick. */
  suggestion?: LocationSuggestion | null;
}

const isLocationSuggestion = (value: unknown): value is LocationSuggestion => {
  if (typeof value !== 'object' || value === null) return false;
  const record: Record<string, unknown> = { ...value };
  return (
    typeof record.name === 'string' &&
    hasValidCoordinates(record) &&
    isNullableString(record.place_formatted) &&
    isNullableString(record.country_code) &&
    isNullableString(record.region) &&
    isNullableString(record.city)
  );
};

const suggestionsIn = (payload: unknown): LocationSuggestion[] => {
  if (typeof payload !== 'object' || payload === null) return [];
  const { data }: { data?: unknown } = { ...payload };
  return Array.isArray(data) ? data.filter(isLocationSuggestion) : [];
};

const boundedLabel = (suggestion: LocationSuggestion, maxLength?: number): string => {
  const full = formatLocationSuggestion(suggestion);
  if (!maxLength || full.length <= maxLength) return full;
  const country = suggestion.place_formatted?.split(', ').at(-1);
  const short = [suggestion.name, country].filter(Boolean).join(', ');
  return (short.length <= maxLength ? short : suggestion.name).slice(0, maxLength);
};

const useLocationSuggestions = (query: string, language: string): LocationSuggestion[] => {
  const [suggestions, setSuggestions] = useState<LocationSuggestion[]>([]);

  useEffect(() => {
    setSuggestions([]);
    const trimmed = query.trim();
    if (trimmed.length < MIN_QUERY_LENGTH) return;

    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const params = new URLSearchParams({ q: trimmed, language });
        const response = await fetch(`${SUGGESTIONS_PATH}?${params}`, { signal: controller.signal });
        if (response.ok) setSuggestions(suggestionsIn(await response.json()));
      } catch {}
    }, DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [query, language]);

  return suggestions;
};

export const LocationPicker: FC<LocationPickerProps> = ({
  id,
  label,
  value,
  onChange,
  placeholder,
  hint,
  error,
  disabled,
  required,
  maxLength,
  suggestion,
}) => {
  const { t, i18n } = useTranslation();
  const [query, setQuery] = useState('');
  const suggestions = useLocationSuggestions(query, i18n.language.slice(0, 2));
  const describedBy = fieldStatusId({ id, error, hint });
  const offeredSuggestion = value.trim() ? null : suggestion;

  const handleSelect = (picked: LocationSuggestion | null) => {
    if (!picked) return;
    setQuery('');
    onChange({ location: boundedLabel(picked, maxLength), geolocation: picked });
  };

  return (
    <Combobox value={null} onChange={handleSelect} disabled={disabled} nullable>
      <div className="relative flex flex-col gap-1.5">
        <Combobox.Label className="text-[13px] font-medium leading-4 tracking-[-0.13px] text-black/70">
          {label}
          {required ? <span aria-hidden="true"> *</span> : null}
        </Combobox.Label>
        <Combobox.Input
          id={id}
          displayValue={() => value}
          placeholder={placeholder}
          maxLength={maxLength}
          required={required}
          autoComplete="off"
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          onChange={(event) => {
            setQuery(event.target.value);
            onChange({ location: event.target.value, geolocation: null });
          }}
          className={cn(
            'h-11 w-full rounded-lg border bg-white px-4 text-[14px] leading-5 tracking-[-0.14px] text-black placeholder:text-black/30 focus:outline-none focus:ring-2 disabled:opacity-60',
            error
              ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
              : 'border-black/10 focus:border-community-primary focus:ring-community-primary/20',
          )}
        />
        {suggestions.length > 0 && (
          <Combobox.Options className="absolute top-full z-20 mt-1 w-full overflow-hidden rounded-2xl border border-black/5 bg-white py-1 shadow-[0_4px_6px_rgba(0,0,0,0.08)] focus:outline-none">
            {suggestions.map((option) => (
              <Combobox.Option
                key={`${option.latitude},${option.longitude}`}
                value={option}
                className={({ active }) =>
                  cn(
                    'flex h-10 w-full cursor-pointer items-center px-4 text-left text-sm tracking-[-0.14px] text-black',
                    { 'bg-black/5': active },
                  )
                }
              >
                <span className="truncate">
                  <span className="font-medium">{option.name}</span>
                  {option.place_formatted && <span className="text-black/50">, {option.place_formatted}</span>}
                </span>
              </Combobox.Option>
            ))}
          </Combobox.Options>
        )}
        {offeredSuggestion && (
          <PillButton
            variant="secondary"
            size="sm"
            className="self-start"
            disabled={disabled}
            onClick={() => handleSelect(offeredSuggestion)}
          >
            <MapPinIcon className="mr-1.5 size-3.5 shrink-0 text-black/40" aria-hidden="true" />
            {t('community.shared.use_suggested_location', { location: boundedLabel(offeredSuggestion, maxLength) })}
          </PillButton>
        )}
        <FieldStatus id={id} error={error} hint={hint} />
      </div>
    </Combobox>
  );
};
